import { SportsModeServiceRepository } from '@/__core__/repository/sports-mode-service.repository'
import { extendDictionary, initLocalization, loc } from '@/__core__/localization/localization'
import { defineStore } from 'pinia'
import { allSettled } from '@/__core__/utils/fetch'

const shouldScheduleRefresh = initialData => {
  if (initialData.isGameLive) {
    return true
  }

  const currentTime = Date.now()
  const eventStartTime = new Date(initialData.startTime).getTime()

  const timeOffset = 30 * 1000 // 30 seconds before the event starts, start data refresh

  return Math.abs(eventStartTime - currentTime) <= timeOffset || (Date.now() > new Date(initialData.startTime).getTime() && !initialData.played)
}

/** @typedef {{ playerId: string, languageIdentifier: string }} Payload */
export const useStatisticsStore = defineStore('Statistics', {
  state: () => ({
    initial: {},
    home: {
      club: {
        abbreviatedName: ''
      }
    },
    away: {
      club: {
        abbreviatedName: ''
      }
    },
    periodOverview: {
      home: {
        name: ''
      },
      away: {
        name: ''
      },
      periods: []
    },
    lineChart: [],
    cardsStats: {
      twoPoints: {
        home: {
          percentage: 'NA',
          attempted: '-',
          made: '-'
        },
        away: {
          percentage: 'NA',
          attempted: '-',
          made: '-'
        },
        label: 'sm_two_points'
      },
      threePoints: {
        home: {
          percentage: 'NA',
          attempted: '-',
          made: '-'
        },
        away: {
          percentage: 'NA',
          attempted: '-',
          made: '-'
        },
        label: 'sm_three_points'
      },
      freeThrows: {
        home: {
          percentage: 'NA',
          attempted: '-',
          made: '-'
        },
        away: {
          percentage: 'NA',
          attempted: '-',
          made: '-'
        },
        label: 'sm_free_throws'
      }
    },

    langId: '',

    drfRetryCounter: 0
  }),

  getters: {
    getShouldStopRefresh() {
      return this.drfRetryCounter >= 10
    }
  },

  actions: {
    getShouldCallApi() {
      return shouldScheduleRefresh(this.initial)
    },
    /** @param {Payload} payload */
    async fetchStatisticsData(payload) {
      try {
        const [, responseLocalization, responseLocalizationBoxscore, responseStatistics] = await allSettled([
          initLocalization(payload.languageIdentifier),
          SportsModeServiceRepository.Localization.get({ languageIdentifier: payload.languageIdentifier, widgetId: 'statistics' }),
          SportsModeServiceRepository.Localization.get({ languageIdentifier: payload.languageIdentifier, widgetId: 'boxscore' }),
          SportsModeServiceRepository.Euroleague.MatchStats.get(payload)
        ])

        if (!responseLocalization || !responseLocalizationBoxscore) {
          throw new Error('Localization data not found')
        }

        extendDictionary(responseLocalization.data, payload.languageIdentifier)
        extendDictionary(responseLocalizationBoxscore.data, payload.languageIdentifier)

        if (!responseStatistics) {
          throw new Error('Statistics data not found')
        }

        this.updateStatistics(responseStatistics.data, payload.languageIdentifier)
      } catch (error) {
        throw new Error(`[Statistics.store, fetchStatisticsData]: ${error.message}`)
      }
    },

    /** @param {Payload} payload */
    async scheduleStatisticsRefresh(payload) {
      SportsModeServiceRepository.Euroleague.MatchStats.get(payload)
        .then(response => this.updateStatistics(response.data, payload.languageIdentifier))
        .catch(() => {
          this.drfRetryCounter++
        })
    },

    updateStatistics(statisticsData, langId = '') {
      let matchStatus = ''

      if (statisticsData.live) {
        matchStatus = loc('sm_live', langId)
      } else if (statisticsData.played) {
        matchStatus = loc('sm_full_time_short', langId)
      }

      this.initial = {
        sportType: 'basketball',
        headerData: {
          home: {
            teamId: statisticsData?.local?.club?.code,
            name: statisticsData?.local?.club?.name,
            shortName: statisticsData?.local?.club?.tvCode,
            score: statisticsData?.local?.score,
            logo: statisticsData?.local?.club?.images?.crest ? `${statisticsData?.local?.club?.images?.crest}?width=60&height=60&resizeType=fill&format=webp` : ''
          },
          away: {
            teamId: statisticsData?.road?.club?.code,
            name: statisticsData?.road?.club?.name,
            shortName: statisticsData?.road?.club?.tvCode,
            score: statisticsData?.road?.score,
            logo: statisticsData?.road?.club?.images?.crest ? `${statisticsData?.road?.club?.images?.crest}?width=60&height=60&resizeType=fill&format=webp` : ''
          }
        },
        isGameLive: statisticsData.live,
        startTime: statisticsData.date,
        played: statisticsData.played,
        matchStatus
      }

      this.home = statisticsData.local
      this.away = statisticsData.road

      const periodsHome = statisticsData.local.partials || {}
      const periodsAway = statisticsData.road.partials || {}
      const extraTimesOriginal = periodsHome.extraPeriods || {}
      const extraTimes = []

      Object.keys(extraTimesOriginal).forEach(key => {
        extraTimes.push({
          home: periodsHome.extraPeriods?.[key] || '-',
          away: periodsAway.extraPeriods?.[key] || '-',
          periodLabel: `OT${key}`
        })
      }) || []

      const periodOverview = {
        home: {
          name: statisticsData?.local?.club?.abbreviatedName
        },
        away: {
          name: statisticsData?.road?.club?.abbreviatedName
        },
        periods: [
          {
            home: periodsHome?.partials1 || '-',
            away: periodsAway?.partials1 || '-',
            periodLabel: 1
          },
          {
            home: periodsHome?.partials2 || '-',
            away: periodsAway?.partials2 || '-',
            periodLabel: 2
          },
          {
            home: periodsHome?.partials3 || '-',
            away: periodsAway?.partials3 || '-',
            periodLabel: 3
          },
          {
            home: periodsHome?.partials4 || '-',
            away: periodsAway?.partials4 || '-',
            periodLabel: 4
          },
          ...extraTimes
        ]
      }

      this.periodOverview = periodOverview

      const statsHome = statisticsData.local.stats || {}
      const statsAway = statisticsData.road.stats || {}

      this.cardsStats = {
        twoPoints: {
          home: {
            percentage: (statsHome.fieldGoals2Percentage && Math.round(statsHome.fieldGoals2Percentage) + '%') || 'NA',
            attempted: statsHome.fieldGoalsAttempted2 || '-',
            made: statsHome.fieldGoalsMade2 || '-'
          },
          away: {
            percentage: (statsAway.fieldGoals2Percentage && Math.round(statsAway.fieldGoals2Percentage) + '%') || 'NA',
            attempted: statsAway.fieldGoalsAttempted2 || '-',
            made: statsAway.fieldGoalsMade2 || '-'
          },
          label: 'sm_two_points'
        },
        threePoints: {
          home: {
            percentage: (statsHome.fieldGoals3Percentage && Math.round(statsHome.fieldGoals3Percentage) + '%') || 'NA',
            attempted: statsHome.fieldGoalsAttempted3 || '-',
            made: statsHome.fieldGoalsMade3 || '-'
          },
          away: {
            percentage: (statsAway.fieldGoals3Percentage && Math.round(statsAway.fieldGoals3Percentage) + '%') || 'NA',
            attempted: statsAway.fieldGoalsAttempted3 || '-',
            made: statsAway.fieldGoalsMade3 || '-'
          },
          label: 'sm_three_points'
        },
        freeThrows: {
          home: {
            percentage: (statsHome.freeThrowsPercentage && Math.round(statsHome.freeThrowsPercentage) + '%') || 'NA',
            attempted: statsHome.freeThrowsAttempted || '-',
            made: statsHome.freeThrowsMade || '-'
          },
          away: {
            percentage: (statsAway.freeThrowsPercentage && Math.round(statsAway.freeThrowsPercentage) + '%') || 'NA',
            attempted: statsAway.freeThrowsAttempted || '-',
            made: statsAway.freeThrowsMade || '-'
          },
          label: 'sm_free_throws'
        }
      }

      const mapStats = [
        { propName: 'valuation', label: 'sm_pir_short' },
        { propName: 'totalRebounds', label: 'statistics_rebounds' },
        { propName: 'offensiveRebounds', label: 'statistics_rebounds_offensive' },
        { propName: 'defensiveRebounds', label: 'statistics_rebounds_defensive' },
        { propName: 'foulsCommited', label: 'statistics_fouls_committed' },
        { propName: 'assistances', label: 'statistics_assists' },
        { propName: 'blocksFavour', label: 'statistics_blocks' },
        { propName: 'steals', label: 'statistics_steals' },
        { propName: 'turnovers', label: 'statistics_turnover' }
      ]

      const lineChart = []

      mapStats.forEach(prop => {
        const total = Number(statsHome[prop.propName]) + Number(statsAway[prop.propName])
        const chart = {
          propName: prop.propName,
          home: {
            label: statsHome[prop.propName] || '-',
            value: calculatePercentageUsage(statsHome[prop.propName], total)
          },
          away: {
            label: statsAway[prop.propName] || '-',
            value: calculatePercentageUsage(statsAway[prop.propName], total)
          },
          title: loc(prop.label, langId),
          disabled: isNaN(total)
        }

        // if both values are 0, then split chart to 50 / 50 %
        if (chart.home.value === 0 && chart.away.value === 0) {
          chart.home.value = 50
          chart.away.value = 50
        }

        lineChart.push(chart)
      })

      this.lineChart = lineChart

      this.langId = langId
    }
  }
})

function calculatePercentageUsage(value, total) {
  if (isNaN(total) || total <= 0) {
    return 0 // Avoid division by zero and negative totals
  }
  // Calculate percentage usage
  const percentage = (Number(value) / total) * 100
  return Math.min(Math.round(percentage * 100) / 100, 100)
}
